import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { api, formatApiErrorDetail } from "../lib/api";
import PageHeader from "../components/PageHeader";

function addDays(dateStr, days) {
  if (!dateStr) return "";
  const d = new Date(dateStr);
  d.setDate(d.getDate() + days);
  return d.toISOString().slice(0, 10);
}

export default function ProductionPage() {
  const [products, setProducts] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [form, setForm] = useState({
    product_id: "",
    lot_number: "",
    quantity: "",
    production_date: new Date().toISOString().slice(0, 10),
    expiry_date: "",
    customer_id: "",
    notes: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    api.get("/products").then((r) => setProducts(r.data));
    api.get("/customers").then((r) => setCustomers(r.data));
  }, []);

  const onProductChange = (e) => {
    const id = e.target.value;
    const p = products.find((x) => x.id === id);
    setForm((f) => ({
      ...f,
      product_id: id,
      expiry_date: p ? addDays(f.production_date, p.default_shelf_life_days) : f.expiry_date,
    }));
  };

  const onProductionDateChange = (e) => {
    const d = e.target.value;
    const p = products.find((x) => x.id === form.product_id);
    setForm((f) => ({
      ...f,
      production_date: d,
      expiry_date: p ? addDays(d, p.default_shelf_life_days) : f.expiry_date,
    }));
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.product_id) {
      toast.error("Please select a product");
      return;
    }
    setSubmitting(true);
    try {
      const payload = { ...form, quantity: parseInt(form.quantity, 10) || 0 };
      if (!payload.customer_id) delete payload.customer_id;
      const { data } = await api.post("/pallets", payload);
      toast.success(`Pallet ${data.pallet_number} logged`);
      navigate(`/pallets/${data.id}`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed to log pallet");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="production-page">
      <PageHeader eyebrow="Cook line · Packaging" title="Log Production." />
      <div className="p-6 md:p-10">
        <form onSubmit={submit} className="mj-card max-w-3xl space-y-6" data-testid="production-form">
          <div className="grid md:grid-cols-2 gap-5">
            <div>
              <label className="data-label block mb-2">Product</label>
              <select
                value={form.product_id}
                onChange={onProductChange}
                className="mj-input"
                required
                data-testid="production-product-select"
              >
                <option value="">Select product…</option>
                {products.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.sku} — {p.name}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="data-label block mb-2">Lot / Batch #</label>
              <input
                className="mj-input font-mono-id"
                value={form.lot_number}
                onChange={(e) => setForm({ ...form, lot_number: e.target.value })}
                required
                placeholder="L-2026-0142"
                data-testid="production-lot-input"
              />
            </div>
            <div>
              <label className="data-label block mb-2">Quantity (cases)</label>
              <input
                className="mj-input font-mono-id"
                type="number"
                min="1"
                value={form.quantity}
                onChange={(e) => setForm({ ...form, quantity: e.target.value })}
                required
                placeholder="0"
                data-testid="production-quantity-input"
              />
            </div>
            <div>
              <label className="data-label block mb-2">Customer (optional)</label>
              <select
                value={form.customer_id}
                onChange={(e) => setForm({ ...form, customer_id: e.target.value })}
                className="mj-input"
                data-testid="production-customer-select"
              >
                <option value="">Unassigned</option>
                {customers.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="data-label block mb-2">Cooked / Packaged date</label>
              <input
                type="date"
                className="mj-input font-mono-id"
                value={form.production_date}
                onChange={onProductionDateChange}
                required
                data-testid="production-production-date"
              />
            </div>
            <div>
              <label className="data-label block mb-2">Expiry date</label>
              <input
                type="date"
                className="mj-input font-mono-id"
                value={form.expiry_date}
                onChange={(e) => setForm({ ...form, expiry_date: e.target.value })}
                required
                data-testid="production-expiry-date"
              />
            </div>
          </div>
          <div>
            <label className="data-label block mb-2">Notes</label>
            <textarea
              className="mj-input"
              style={{ minHeight: 90, padding: "10px 12px" }}
              value={form.notes}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
              placeholder="Optional notes for this pallet"
              data-testid="production-notes-input"
            />
          </div>
          <div className="flex items-center gap-3 pt-2">
            <button type="submit" disabled={submitting} className="mj-btn mj-btn-primary" data-testid="production-submit-btn">
              {submitting ? "Logging…" : "Log Pallet + Generate Barcode"}
            </button>
            <button
              type="button"
              onClick={() => navigate(-1)}
              className="mj-btn mj-btn-ghost"
              data-testid="production-cancel-btn"
            >
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
